import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import PerformanceGraph from '../components/PerformanceGraph';
import { getAdAccounts, getWinningAds, getWpStatus } from '../api';
import { PageKey } from '../components/Layout';

interface AdAccount {
  id: string;
  name: string;
  active: boolean;
  business_name?: string;
  page_name?: string;
  location?: string;
  campaigns: { count: number; names: string[]; ids: string[]; all_names: string[] };
}

interface Props {
  user: { email: string; name: string };
  onLogout: () => void;
  onNavigate: (page: PageKey) => void;
  onOpenAccount: (accountId: string, accountName: string) => void;
}

const isNumeric = (s?: string | null) => !!s && /^\d+$/.test(s.trim());

function resolved(acc: AdAccount): string {
  // Priority: business_name > page_name > account_name (if non-numeric) > account_name > id
  if (acc.business_name?.trim() && !isNumeric(acc.business_name)) return acc.business_name.trim();
  if (acc.page_name?.trim()) return acc.page_name.trim();
  if (acc.name?.trim() && !isNumeric(acc.name)) return acc.name.trim();
  if (acc.name?.trim()) return acc.name.trim();
  return acc.id.replace('act_', '');
}

// Module-level cache — survives navigation (component unmount/remount)
let _cache: AdAccount[] | null = null;
let _cacheTs = 0;

function timeAgo(ts: number): string {
  if (!ts) return '';
  const mins = Math.floor((Date.now() - ts) / 60000);
  if (mins < 1) return 'gerade eben';
  if (mins === 1) return 'vor 1 Min';
  return `vor ${mins} Min`;
}

export default function Dashboard({ user, onLogout, onNavigate, onOpenAccount }: Props) {
  const [accounts, setAccounts] = useState<AdAccount[]>(_cache || []);
  const [loading, setLoading] = useState(!_cache);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [wpOnly, setWpOnly] = useState(false);
  const [wpStatus, setWpStatus] = useState<Record<string, { has_config: boolean; wp_status: string; wp_status_msg?: string; wp_url?: string }>>({});
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState(_cacheTs);
  const [winningAds, setWinningAds] = useState<Record<string, { name: string; leads: number }>>({});
  const [debouncedFilter, setDebouncedFilter] = useState<{ accountIds?: string[]; pattern?: string }>({});
  const [, tick] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Tick every 30s so "vor X Min" stays current
  useEffect(() => {
    const t = setInterval(() => tick(n => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => { load(); }, []);
  useEffect(() => { getWpStatus().then(r => setWpStatus(r.data || {})).catch(() => {}); }, []);

  // WP-Verbindungsstatus pro Account: 'connected' | 'error' | 'none'
  const wpState = (accId: string): 'connected' | 'error' | 'none' => {
    const e = wpStatus[accId];
    if (!e || !e.has_config) return 'none';
    return e.wp_status === 'connected' ? 'connected' : 'error';
  };

  const load = async (force = false) => {
    if (_cache && !force) return; // use cache on navigation-back
    setLoading(true); setError('');
    try {
      const res = await getAdAccounts();
      _cache = res.data.accounts;
      _cacheTs = Date.now();
      setAccounts(res.data.accounts);
      setLastUpdated(_cacheTs);
      // Fetch winning ads for all active campaigns (non-blocking)
      const allIds = (res.data.accounts as AdAccount[]).flatMap(a => a.campaigns.ids || []);
      if (allIds.length > 0) {
        getWinningAds(allIds).then(r => setWinningAds(r.data)).catch(() => {});
      }
    } catch (e: any) {
      const msg = String(e.response?.data?.error || e.message || '');
      // Meta-Drosselung/Sperre nicht als harten Fehler zeigen – Dashboard leer lassen
      if (/block|limit|throttl|reduce the amount/i.test(msg)) {
        setError('');
      } else {
        setError(e.response?.data?.error || 'Fehler beim Laden');
      }
    } finally {
      setLoading(false);
    }
  };

  const searchActive = search.trim().length > 0;
  const q = search.toLowerCase().trim();

  const matchesByName = (acc: AdAccount) =>
    resolved(acc).toLowerCase().includes(q) ||
    (acc.page_name || '').toLowerCase().includes(q) ||
    (acc.business_name || '').toLowerCase().includes(q) ||
    (acc.location || '').toLowerCase().includes(q) ||
    acc.id.replace('act_', '').includes(q);

  const matchesByCampaign = (acc: AdAccount) => {
    // Aktiv-Modus: nur aktive Kampagnennamen prüfen; Alle-Modus: alle inkl. pausierter
    const names = showAll
      ? (acc.campaigns.all_names || acc.campaigns.names)
      : acc.campaigns.names;
    return names.some(n => n.toLowerCase().includes(q));
  };

  // Filtered list
  const filtered = accounts.filter(acc => {
    if (!acc.active) return false;
    if (wpOnly && wpState(acc.id) === 'connected') return false;
    if (!showAll && !searchActive && !wpOnly && acc.campaigns.count === 0) return false;
    if (!searchActive) return true;
    return matchesByName(acc) || matchesByCampaign(acc);
  });

  // For campaign filter display — accounts that matched via campaign search
  const campFilterActive = searchActive && filtered.some(matchesByCampaign);
  const campFilter = campFilterActive ? search.trim() : '';

  const displayAccounts = searchActive
    ? filtered
    : accounts.filter(a => a.active && (showAll || a.campaigns.count > 0));
  const totalCamps = displayAccounts.reduce((s, a) => s + (a.campaigns?.count || 0), 0);

  const activeWithCamps = accounts.filter(a => a.active && a.campaigns.count > 0).length;
  const subtitleCount = searchActive
    ? `${filtered.length} von ${accounts.filter(a => a.active).length}`
    : showAll
    ? `${accounts.filter(a => a.active).length}`
    : `${activeWithCamps} mit Kampagnen`;

  // Debounce graph filter
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (campFilterActive) {
        setDebouncedFilter({ accountIds: filtered.map(a => a.id), pattern: search.trim() });
      } else if (searchActive) {
        setDebouncedFilter({ accountIds: filtered.map(a => a.id) });
      } else {
        setDebouncedFilter({});
      }
    }, 600);
  }, [search, filtered.length]);

  return (
    <Layout user={user} onLogout={onLogout} activePage="dashboard" onNavigate={onNavigate}>
      <div style={s.page}>
        {/* Header */}
        <div style={s.header}>
          <div>
            <h1 style={s.title}>DASHBOARD</h1>
            <p style={s.sub}>Meta · {subtitleCount} Ad Accounts</p>
          </div>
          <div style={s.headerRight}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
              <button onClick={() => load(true)} style={s.refreshBtn}><RefreshIcon /> Aktualisieren</button>
              {lastUpdated > 0 && (
                <span style={{ fontSize: 11, color: '#aeaeb2' }}>letzte Aktualisierung: {timeAgo(lastUpdated)}</span>
              )}
            </div>
          </div>
        </div>

        {/* Stats + Graph */}
        {!loading && accounts.length > 0 && (
          <div style={s.topRow}>
            <div style={s.statsCol}>
              <div style={s.statCard}>
                <div style={s.statNum}>{displayAccounts.length}</div>
                <div style={s.statLabel}>Ad Accounts</div>
              </div>
              <div style={s.statCard}>
                <div style={{ ...s.statNum, color: '#1a3a5c' }}>{totalCamps}</div>
                <div style={s.statLabel}>Aktive Kampagnen</div>
              </div>
            </div>
            <PerformanceGraph filteredAccountIds={debouncedFilter.accountIds} campaignPattern={debouncedFilter.pattern} />
          </div>
        )}

        {/* Filters */}
        {!loading && accounts.length > 0 && (
          <div style={s.filterRow}>
            <div style={{ ...s.searchBox, maxWidth: 480 }}>
              <SearchIcon />
              <input
                type="text" placeholder="Suche nach Name, Ort, Kampagne…"
                value={search} onChange={e => setSearch(e.target.value)}
                style={{ ...s.input, borderColor: searchActive ? '#1a3a5c' : '#d2d2d7' }}
              />
              {searchActive && (
                <span style={s.matchBadge}>{filtered.length} Treffer</span>
              )}
              {search && <button onClick={() => setSearch('')} style={s.clearBtn}>✕</button>}
            </div>
            <div style={s.toggle}>
              <button
                onClick={() => setShowAll(false)}
                style={{ ...s.tBtn, ...(!showAll ? s.tBtnActive : {}) }}
              >
                Aktive
              </button>
              <button
                onClick={() => setShowAll(true)}
                style={{ ...s.tBtn, ...(showAll ? s.tBtnActive : {}) }}
              >
                Alle
              </button>
            </div>
            <button
              onClick={() => setWpOnly(v => !v)}
              style={{ ...s.wpFilterBtn, ...(wpOnly ? s.wpFilterBtnActive : {}) }}
              title="Nur Partner ohne funktionierende WordPress-Verbindung anzeigen"
            >
              <span style={{ color: wpOnly ? '#fff' : '#ff3b30', fontWeight: 700 }}>!</span>
              Ohne WordPress
            </button>
          </div>
        )}

        {loading && (
          <div style={s.center}>
            <div style={s.spinner} />
            <span style={{ color: '#6e6e73', marginLeft: 12 }}>Lade alle Ad Accounts…</span>
          </div>
        )}
        {error && <div style={s.errBox}><strong>Fehler:</strong> {error}</div>}

        {/* Grid */}
        {!loading && !error && (
          <div style={s.grid}>
            {filtered.map(acc => {
              const name = resolved(acc);
              const numId = acc.id.replace('act_', '');
              const camps = acc.campaigns || { count: 0, names: [], all_names: [] };
              return (
                <div
                  key={acc.id}
                  style={{
                    ...s.card,
                    ...(!acc.active ? s.cardInactive : {}),
                    cursor: 'pointer',
                    ...(hoveredCard === acc.id ? s.cardHover : {}),
                  }}
                  onClick={() => onOpenAccount(acc.id, name)}
                  onMouseEnter={() => setHoveredCard(acc.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  <div style={s.cardTop}>
                    <div style={s.cardName}>
                      {name}
                      {!acc.active && <span style={s.inactiveDot} title="Inaktiver Account" />}
                      <a
                        href={`https://adsmanager.facebook.com/adsmanager/manage/campaigns?act=${numId}`}
                        target="_blank" rel="noreferrer"
                        style={s.extLink}
                        onClick={e => e.stopPropagation()}
                        title="Im Ads Manager öffnen"
                      >
                        <ExternalIcon />
                      </a>
                    </div>
                  </div>
                  <div style={s.cardId}>{numId}{acc.location ? ` · ${acc.location}` : ''}</div>
                  {(() => {
                    const h = wpStatus[acc.id]?.wp_url;
                    return h ? <div style={{ fontSize: 11, color: '#34c759', marginTop: 2 }}>{h.replace(/^https?:\/\//, '')}</div> : null;
                  })()}

                  <div style={s.campMeta}>
                    <div style={s.metaLabel}>Aktive Kampagnen</div>
                    <div style={{ ...s.metaNum, color: camps.count > 0 ? '#1a3a5c' : '#aeaeb2' }}>
                      {camps.count}
                    </div>
                  </div>

                  {(() => {
                    const activeSet = new Set(camps.names);
                    const displayCamps = campFilterActive
                      ? (camps.all_names || [])
                          .filter(n => n.toLowerCase().includes(campFilter.toLowerCase()))
                          .slice(0, 3)
                          .map(n => ({ name: n, active: activeSet.has(n) }))
                      : camps.names.slice(0, 3).map(n => ({ name: n, active: true }));
                    return displayCamps.length > 0 ? (
                      <div style={s.campList}>
                        {displayCamps.map(({ name: cn, active: isAct }, i) => {
                          const campId = (camps.ids || [])[i];
                          const winner = campId ? winningAds[campId] : undefined;
                          return (
                            <div key={i} style={{ marginBottom: winner ? 6 : 0 }}>
                              <div style={s.campRow}>
                                <span style={{ ...s.dot, background: isAct ? '#34c759' : '#d2d2d7' }} />
                                <span style={{ ...s.campName, ...(isAct ? {} : { color: '#aeaeb2' }) }}>{cn}</span>
                              </div>
                              {winner && (
                                <div style={s.winnerRow}>
                                  🏆 {winner.name} · {winner.leads} Leads
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : null;
                  })()}

                  <div style={s.detailBtn}>Profil ansehen →</div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && !error && filtered.length === 0 && accounts.length > 0 && (
          <div style={s.center}>
            <span style={{ color: '#6e6e73' }}>
              {search ? `Keine Ergebnisse für „${search}"` : 'Keine Accounts gefunden'}
            </span>
          </div>
        )}

        {!loading && !error && accounts.length === 0 && (
          <div style={{ textAlign: 'center' as const, padding: '70px 20px', color: '#6e6e73' }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#1d1d1f', marginBottom: 6 }}>Daten gerade nicht verfügbar</div>
            <div style={{ fontSize: 13, maxWidth: 440, margin: '0 auto', lineHeight: 1.5 }}>
              Meta drosselt aktuell den Zugriff (zu viele Anfragen). Das Dashboard füllt sich automatisch wieder,
              sobald Meta den Zugriff freigibt – meist innerhalb weniger Stunden. Du musst nichts tun.
            </div>
            <button onClick={() => load(true)} style={{ ...s.refreshBtn, margin: '16px auto 0' }}>
              <RefreshIcon /> Erneut versuchen
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
}

const SearchIcon = () => (
  <svg style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#aeaeb2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

const RefreshIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}>
    <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
  </svg>
);
const ExternalIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
    <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
  </svg>
);

const s: Record<string, React.CSSProperties> = {
  page: { padding: '44px 48px 64px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  title: { fontSize: 26, fontWeight: 800, color: '#1d1d1f', letterSpacing: '0.04em' },
  sub: { fontSize: 13, color: '#6e6e73', marginTop: 4 },
  headerRight: { display: 'flex', alignItems: 'center', gap: 10 },
  toggle: {
    display: 'flex', background: '#f5f5f7', borderRadius: 10,
    padding: 3, border: '1px solid #e8e8ed',
  },
  tBtn: {
    background: 'transparent', border: 'none', borderRadius: 8,
    padding: '6px 12px', fontSize: 12, fontWeight: 500, cursor: 'pointer', color: '#6e6e73',
    whiteSpace: 'nowrap' as const,
  },
  tBtnActive: { background: '#fff', color: '#1a3a5c', fontWeight: 600, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  wpFilterBtn: {
    display: 'flex', alignItems: 'center', gap: 6, background: '#fff',
    border: '1px solid #d2d2d7', borderRadius: 10, padding: '7px 12px',
    fontSize: 12, fontWeight: 500, cursor: 'pointer', color: '#6e6e73', whiteSpace: 'nowrap' as const,
  },
  wpFilterBtnActive: { background: '#1a3a5c', color: '#fff', borderColor: '#1a3a5c' },
  wpBadge: {
    width: 16, height: 16, borderRadius: '50%', color: '#fff', fontSize: 11, fontWeight: 800,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    lineHeight: 1,
  },
  refreshBtn: {
    display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #d2d2d7',
    borderRadius: 10, color: '#1d1d1f', padding: '8px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 500,
  },
  topRow: { display: 'flex', gap: 16, marginBottom: 22, alignItems: 'stretch' },
  statsCol: { display: 'flex', flexDirection: 'column', gap: 12, flexShrink: 0, width: 160 },
  statCard: {
    background: '#fff', border: '1px solid #e8e8ed', borderRadius: 14,
    padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', flex: 1,
  },
  statNum: { fontSize: 28, fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.5px' },
  statLabel: { fontSize: 11, color: '#6e6e73', marginTop: 3 },
  filterRow: { display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' as const },
  searchBox: { position: 'relative' as const, flex: '1 1 240px', maxWidth: 360 },
  input: {
    width: '100%', background: '#fff', border: '1.5px solid #d2d2d7',
    borderRadius: 10, padding: '9px 36px', color: '#1d1d1f', outline: 'none', fontSize: 13,
  },
  clearBtn: {
    position: 'absolute' as const, right: 10, top: '50%', transform: 'translateY(-50%)',
    background: 'transparent', border: 'none', color: '#aeaeb2', cursor: 'pointer', fontSize: 12, padding: 2,
  },
  matchBadge: {
    position: 'absolute' as const, right: 28, top: '50%', transform: 'translateY(-50%)',
    fontSize: 11, color: '#1a3a5c', fontWeight: 600, background: 'rgba(26,58,92,0.08)',
    borderRadius: 5, padding: '1px 6px',
  },
  center: { display: 'flex', alignItems: 'center', padding: '60px 0' },
  spinner: {
    width: 24, height: 24, border: '2.5px solid #e8e8ed',
    borderTop: '2.5px solid #1a3a5c', borderRadius: '50%', animation: 'spin 0.7s linear infinite',
  },
  errBox: {
    background: '#fff2f2', border: '1px solid rgba(255,59,48,0.2)',
    borderRadius: 12, padding: '14px 18px', color: '#ff3b30', marginBottom: 20, fontSize: 13,
  },
  grid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))',
    gap: 14, animation: 'fadeIn 0.2s ease',
  },
  card: {
    background: '#fff', border: '1px solid #e8e8ed', borderRadius: 16,
    padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
    transition: 'box-shadow 0.15s, transform 0.15s',
    display: 'flex', flexDirection: 'column',
  },
  cardInactive: { background: '#fafafa' },
  cardHover: { boxShadow: '0 4px 16px rgba(0,0,0,0.10)', transform: 'translateY(-1px)' },
  winnerRow: {
    fontSize: 10.5, color: '#aeaeb2', marginLeft: 11, marginTop: 2,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const,
  },
  cardTop: { display: 'flex', alignItems: 'flex-start', marginBottom: 3 },
  cardName: {
    fontSize: 13.5, fontWeight: 600, color: '#1d1d1f', flex: 1,
    display: 'flex', alignItems: 'center', gap: 5,
  },
  inactiveDot: {
    width: 6, height: 6, borderRadius: '50%', background: '#d2d2d7', flexShrink: 0,
  },
  extLink: { color: '#aeaeb2', display: 'flex', alignItems: 'center', flexShrink: 0 },
  cardId: { fontSize: 11, color: '#aeaeb2', marginBottom: 14 },
  campMeta: {},
  metaLabel: { fontSize: 11, color: '#aeaeb2', marginBottom: 2 },
  metaNum: { fontSize: 17, fontWeight: 700, color: '#1d1d1f' },
  campList: {
    marginTop: 10, paddingTop: 10, borderTop: '1px solid #f0f0f5',
    display: 'flex', flexDirection: 'column', gap: 4, flex: 1,
  },
  campRow: { display: 'flex', alignItems: 'center', gap: 6 },
  dot: { width: 5, height: 5, borderRadius: '50%', flexShrink: 0 },
  campName: {
    fontSize: 11.5, color: '#6e6e73', flex: 1,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const,
  },
  detailBtn: {
    marginTop: 14, background: 'transparent', border: '1px solid #e8e8ed',
    borderRadius: 8, color: '#6e6e73', padding: '7px 12px', cursor: 'pointer',
    fontSize: 12, textAlign: 'left' as const,
  },
};
